import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useParts } from '@/hooks/useParts';
import { batchCreateParts } from '@/lib/parts';
import { SAMPLE_PARTS } from '@/data/sampleParts';
import { categoryLabel } from '@/data/categories';
import AdminLayout from '@/components/admin/AdminLayout';

export default function AdminDashboard() {
  const { parts, loading } = useParts();
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);
  const [seedErr, setSeedErr] = useState<string | null>(null);

  const livePartsCount = parts.filter((p) => p.status === 'live').length;
  const reservedCount = parts.filter((p) => p.status === 'reserved').length;
  const draftCount = parts.filter((p) => p.status === 'draft').length;

  const catBuckets = parts.reduce<Record<string, number>>((acc, p) => {
    acc[p.category] = (acc[p.category] ?? 0) + 1;
    return acc;
  }, {});
  const topCats = Object.entries(catBuckets)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);
  const maxCat = topCats[0]?.[1] ?? 1;

  async function handleSeed() {
    setSeedErr(null);
    setSeedMsg(null);
    setSeedBusy(true);
    try {
      const count = await batchCreateParts(SAMPLE_PARTS);
      setSeedMsg(`Seeded ${count} parts to Firestore.`);
    } catch (err) {
      setSeedErr(err instanceof Error ? err.message : String(err));
    } finally {
      setSeedBusy(false);
    }
  }

  return (
    <AdminLayout>
      <div className="eyebrow">Admin · Live Operations</div>
      <h1 className="h-title">Dashboard</h1>
      <p className="lead" style={{ marginBottom: 32 }}>
        Live snapshot of inventory and operations. Subscribers and AI Agents tabs are placeholders
        for now — Sprint 5 wires real data into the Subscribers view.
      </p>

      <div className="stat-row">
        <div className="stat">
          <div className="stat-value">{loading ? '…' : parts.length}</div>
          <div className="stat-label">Total Parts</div>
        </div>
        <div className="stat">
          <div className="stat-value">{loading ? '…' : livePartsCount}</div>
          <div className="stat-label">Live</div>
        </div>
        <div className="stat">
          <div className="stat-value">{loading ? '…' : reservedCount}</div>
          <div className="stat-label">Reserved</div>
        </div>
        <div className="stat">
          <div className="stat-value">{loading ? '…' : draftCount}</div>
          <div className="stat-label">Draft</div>
        </div>
      </div>

      <div className="responsive-grid cols-2-wide" style={{ marginBottom: 36 }}>
        <div className="card-elev">
          <div className="row between" style={{ marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
            <h2 className="h-section" style={{ margin: 0 }}>Inventory Snapshot</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to="/admin/inventory/new" className="btn sm">＋ Add a Part</Link>
              <Link to="/admin/inventory" className="btn ghost sm">Open Manager →</Link>
            </div>
          </div>
          {topCats.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <p>No parts yet. Seed the 16 sample parts below to bootstrap the catalog.</p>
            </div>
          ) : (
            topCats.map(([cat, count]) => {
              const width = Math.round((count / maxCat) * 100);
              return (
                <div key={cat} style={{ marginBottom: 12 }}>
                  <div className="row between" style={{ fontSize: 12.5, marginBottom: 4 }}>
                    <span>{categoryLabel(cat)}</span>
                    <span style={{ fontFamily: 'var(--mono)', color: 'var(--gold)' }}>{count}</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--bg)', border: '1px solid var(--line)' }}>
                    <div style={{ height: '100%', width: `${width}%`, background: 'linear-gradient(90deg, var(--gold-deep), var(--gold))' }} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="card-elev">
          <h2 className="h-section">Seed Sample Catalog</h2>
          <p style={{ fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.55, marginBottom: 14 }}>
            One-shot import of the 16 sample parts (the ones from the legacy demo). Idempotent —
            running again overwrites existing docs with the same SKUs.
          </p>
          <button type="button" className="btn block" onClick={handleSeed} disabled={seedBusy}>
            {seedBusy ? 'Seeding…' : `Seed ${SAMPLE_PARTS.length} Sample Parts →`}
          </button>
          {seedMsg && <div className="auth-success" style={{ marginTop: 12 }}><strong>Done.</strong><p>{seedMsg}</p></div>}
          {seedErr && <div className="auth-error" style={{ marginTop: 12 }}>{seedErr}</div>}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed var(--line-2)' }}>
            <Link to="/admin/inventory/import" className="btn dark sm block">
              Or import your own CSV →
            </Link>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
